<template>
  <div style="padding: 5px">
    <!-- Search -->
    <md-card class="qc-card">
      <md-card-content>
        <div class="qc-search-row">
          <div class="qc-search-left">
            <p class="qc-search-label">Search by</p>
            <div class="search-options">
              <ul>
                <li v-for="opt in searchOptions" :key="opt.value">
                  <input
                    type="radio"
                    name="qc-search-by"
                    :id="`qc-search-by-${opt.value}`"
                    v-model="searchKey"
                    :value="opt.value"
                  />
                  <span>{{ opt.label }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="qc-search-input-wrapper">
            <md-field class="qc-search-field">
              <md-input v-model="searchValue" placeholder="Search Item Here..." />
            </md-field>
          </div>
        </div>
      </md-card-content>
    </md-card>

    <md-divider></md-divider>

    <!-- Table -->
    <md-card class="qc-card">
      <md-card-content>
        <div v-if="showQCBtn" class="row-btns">
          <md-button class="qc-button" @click="toggleMultTable">
            {{ showMultTable ? "Close" : "Multiple QA" }}
          </md-button>
          <md-button
            v-if="showMultTable && selectedItems.length"
            class="qc-button qc-button--confirm"
            @click="$emit('qc-mult', selectedItems)"
          >
            QA {{ selectedItems.length }} Selected
          </md-button>
        </div>

        <div class="qc-table-wrapper">
          <!-- Grouped by line -->
          <table v-if="!showMultTable" class="qc-table">
            <thead>
              <tr>
                <th>#</th>
                <th v-for="col in tableInfo" :key="col.key">{{ col.label }}</th>
                <th>Controls</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!groupedLines.length">
                <td :colspan="colspan" class="qc-empty">No items found</td>
              </tr>

              <template v-for="group in groupedLines">
                <tr :key="`line-${group.lineNum}`" class="qc-line-header">
                  <td :colspan="colspan">
                    Line {{ group.lineNum }}
                    <span class="qc-line-count">
                      {{ group.items.length }} item{{ group.items.length === 1 ? "" : "s" }}
                    </span>
                  </td>
                </tr>

                <tr
                  v-for="(item, index) in group.items"
                  :key="`item-${group.lineNum}-${item.StockNum || index}`"
                  class="qc-main-row"
                >
                  <td>{{ index + 1 }}</td>
                  <td v-for="col in tableInfo" :key="col.key">
                    {{ display(item[col.key]) }}
                  </td>
                  <td>
                    <div class="qc-controls">
                      <md-button class="qc-button" @click="$emit('open-qc', item)">QA</md-button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- Multi-select -->
          <md-table
            v-else-if="showQCBtn && showMultTable"
            v-model="multRows"
            md-card
            @md-selected="onSelected"
          >
            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              :md-disabled="item.QCCompleted"
              md-selectable="multiple"
              md-auto-select
            >
              <md-table-cell
                v-for="col in tableInfo"
                :key="col.key"
                :md-label="col.label"
                :md-sort-by="col.key"
              >
                {{ display(item[col.key]) }}
              </md-table-cell>
            </md-table-row>
          </md-table>
        </div>
      </md-card-content>

      <md-card-actions md-alignment="space-between">
        <p class="card-category">
          Showing {{ total ? from + 1 : 0 }} to {{ to }} of {{ total }} entries
        </p>

        <Pagination
          class="pagination-no-border pagination-success"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />

        <md-table-toolbar class="per-page">
          <md-field>
            <label for="qc-per-page">Per page</label>
            <md-select v-model="pagination.perPage" id="qc-per-page" name="qc-per-page">
              <md-option
                v-for="n in pagination.perPageOptions"
                :key="n"
                :label="n"
                :value="n"
              >{{ n }}</md-option>
            </md-select>
          </md-field>
        </md-table-toolbar>
      </md-card-actions>
    </md-card>
  </div>
</template>

<script>
import Pagination from "./Pagination.vue";

const SEARCH_DEBOUNCE_MS = 250;

export default {
  components: { Pagination },

  props: {
    showQCBtn: Boolean,
    showFilter: Boolean,
    showItemFilter: Boolean,
    showLineItemFilter: Boolean,
    tableData: { type: Array, default: () => [] },
    tableInfo: { type: Array, default: () => [] },
  },

  data() {
    return {
      searchOptions: [
        { label: "Line Number", value: "LineNum" },
        { label: "SKU", value: "SKU" },
        { label: "Certificates", value: "CertNum" },
      ],
      searchKey: "LineNum",
      searchValue: "",
      searchTimer: null,
      showMultTable: false,
      selectedItems: [],
      multRows: [], // local copy so md-table can sort it
      pagination: {
        perPage: 250,
        currentPage: 1,
        perPageOptions: [250, 500, 1000],
      },
    };
  },

  computed: {
    total() {
      return this.tableData.length;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      return Math.min(this.from + this.pagination.perPage, this.total);
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.total / this.pagination.perPage));
    },
    colspan() {
      return this.tableInfo.length + 2;
    },
    /** The current page of rows. */
    queriedData() {
      return this.tableData.slice(this.from, this.to);
    },
    /** The current page grouped by LineNum, in numeric line order. */
    groupedLines() {
      const groups = new Map();
      for (const item of this.queriedData) {
        const lineNum = item.LineNum != null ? item.LineNum : "Unassigned";
        if (!groups.has(lineNum)) groups.set(lineNum, []);
        groups.get(lineNum).push(item);
      }
      return [...groups.entries()]
        .map(([lineNum, items]) => ({ lineNum, items }))
        .sort((a, b) => Number(a.lineNum) - Number(b.lineNum));
    },
  },

  watch: {
    queriedData: {
      immediate: true,
      handler(rows) {
        this.multRows = rows.slice();
      },
    },
    searchValue() {
      this.emitSearchDebounced();
    },
    // Re-run the search when the key changes, not only when the text does.
    searchKey() {
      if (this.searchValue) this.emitSearch();
    },
    "pagination.perPage"() {
      this.pagination.currentPage = 1;
    },
    // Keep the current page in range when search shrinks the data.
    lastPage(last) {
      if (this.pagination.currentPage > last) this.pagination.currentPage = last;
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    display(value) {
      return value === null || value === undefined || value === "" ? "N/A" : value;
    },
    emitSearch() {
      clearTimeout(this.searchTimer);
      this.$emit("handleSearch", {
        searchValue: this.searchValue,
        searchKey: this.searchKey,
      });
    },
    emitSearchDebounced() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.emitSearch, SEARCH_DEBOUNCE_MS);
    },
    toggleMultTable() {
      this.showMultTable = !this.showMultTable;
      if (!this.showMultTable) this.selectedItems = [];
    },
    onSelected(rows) {
      this.selectedItems = rows;
    },
  },
};
</script>

<style scoped>
.qc-card {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
  margin-bottom: 16px;
  padding-top: 4px;
}

/* ===== Search ===== */
.qc-search-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.qc-search-left {
  min-width: 220px;
}

.qc-search-label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.search-options ul {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: #f3f4f6;
  list-style: none;
  margin: 0;
}

.search-options li {
  position: relative;
  margin: 0;
}

.search-options input[type="radio"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.search-options span {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  color: #4b5563;
}

.search-options input[type="radio"]:checked + span {
  background: #253c6e;
  color: #f9fafb;
}

/* Visible focus for keyboard users (the input itself is invisible). */
.search-options input[type="radio"]:focus-visible + span {
  outline: 2px solid #253c6e;
  outline-offset: 2px;
}

/* ===== Buttons ===== */
.row-btns {
  display: flex;
  gap: 8px;
}

.qc-button--confirm {
  background-color: green !important;
}

/* ===== Table ===== */
.qc-table-wrapper {
  max-height: 400px;
  margin-top: 8px;
  overflow: auto;
}

.qc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #ffffff;
}

.qc-table thead {
  position: sticky;
  top: 0;
  z-index: 8;
  background: #253c6e;
}

.qc-table thead th {
  padding: 10px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  color: #ffffff;
  white-space: nowrap;
}

.qc-line-header td {
  padding: 8px 10px;
  background: #eef1f8;
  color: #253c6e;
  font-weight: 600;
  text-align: left;
  border-top: 1px solid #dfe3ee;
}

.qc-line-count {
  margin-left: 8px;
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
}

.qc-main-row td {
  padding: 8px 10px;
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
}

.qc-main-row:hover {
  background: #f3f4ff;
}

.qc-controls {
  display: flex;
  justify-content: center;
  width: 150px;
  margin: 0 auto;
}

.qc-empty {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}

.per-page {
  margin-bottom: 20px;
}
</style>